import React, { useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArrayRenderProps,
  FieldRenderProps,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { MdDelete } from "react-icons/md";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IItem } from "../item/itemModel";
import {
  deleteDullReceiptOrderItems,
  findAllPendingDullOrder,
  getDullReceiptById,
  updateDullReceiptImage,
} from "./services/dullReceipt.services";
import {
  openDullReceiptOrderSelectionDialog,
  setDullReceiptOrderItemIndex,
} from "./dullReceiptSlice";
import FormTextArea from "../../components/formFields/FormTextArea";
import IconButton from "../../components/common/IconButton";
import { LoadingPanel } from "../../components/layout/Loading";
import { ImCross } from "react-icons/im";
import AlertBox from "../../components/common/AlertBox";
import Slider from "react-slick";
import { PiUploadSimpleBold } from "react-icons/pi";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { FaEye } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import FormCheckbox from "../../components/formFields/FormCheckbox";

const DullReceiptOrderItemArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const DullReceiptOrderItemIndex = useAppSelector(
    (state) => state.dullreceipt.DullReceiptOrderItemIndex,
  );
  const karigar_id =
    fieldArrayRenderProps.formRenderProps.valueGetter("karigar_id");
  const name = fieldArrayRenderProps?.name;
  const dull_receipt_guid = fieldArrayRenderProps?.dull_receipt_guid;
  const ItemList = useAppSelector((state) => state.item.ItemList);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index]?.id) {
        const response = await dispatch(
          deleteDullReceiptOrderItems(fieldArrayRenderProps?.value[index].id),
        );
        if (response.meta.requestStatus === "fulfilled") {
          fieldArrayRenderProps.onRemove({ index: index });
          dispatch(closeDialog());
          dispatch(setDullReceiptOrderItemIndex(-1));
          dispatch(findAllPendingDullOrder({ karigar_id: karigar_id }));
        }
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeDialog());
        dispatch(setDullReceiptOrderItemIndex(-1));
      }
    },
    [fieldArrayRenderProps],
  );
  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        order_id: null,
        item_id: null,
        item_description: "",
        issue_weight: null,
        received_weight: null,
        total_charge: null,
        ghat_weight: null,
        amount: null,
        IsVerified: false,
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openDialog("DullReceiptOrderDeleteDialog"));
    dispatch(setDullReceiptOrderItemIndex(index));
  };

  const handleSelectOrder = (index: number) => {
    dispatch(openDullReceiptOrderSelectionDialog());
    dispatch(setDullReceiptOrderItemIndex(index));
  };

  const handleOpenImageUploadDialog = (index: number) => {
    dispatch(openDialog("show-image-upload-dialog"));
    dispatch(setDullReceiptOrderItemIndex(index));
  };

  const handleOpenImage = (url: string) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const orderTableWidth = 2500;
  const orderTableColimn = [
    { width: "5%" },
    { width: "5%" },
    { width: "10%" },
    { width: "10%" },
    { width: "10%" },
    { width: "10%" },
    { width: "10%" },
    { width: "10%" },
    { width: "9%" },
    { width: "6%" },
    { width: "6%" },
    { width: "5%" },
    { width: "2%" },
  ];

  const OrderNoField = (props: FieldRenderProps) => {
    const { value } = props;
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "18px",
        }}
      >
        {value}
      </div>
    );
  };

  return (
    <>
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <GridLayout
            cols={[{ width: "100%" }]}
            style={{ overflowX: "scroll", scrollbarWidth: "thin" }}
          >
            <ImageUploadDialog fieldArrayRenderProps={fieldArrayRenderProps} />
            {dialogName === "DullReceiptOrderDeleteDialog" && (
              <AppDialog>
                <>
                  <Typography.h5>{"Delete Order"}</Typography.h5>
                  <GridLayout>
                    <GridLayoutItem>
                      <Typography.p>
                        Are you sure you want to delete this?
                      </Typography.p>
                    </GridLayoutItem>
                    <GridLayoutItem
                      style={{
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      <DialogCloseButton themeColor="error" />
                      <Button
                        fillMode={"solid"}
                        themeColor={"error"}
                        onClick={() =>
                          handleDeleteAction(DullReceiptOrderItemIndex)
                        }
                        type="button"
                      >
                        Delete
                      </Button>
                    </GridLayoutItem>
                  </GridLayout>
                </>
              </AppDialog>
            )}
            <GridLayoutItem
              className="bg-dark text-white fw-600"
              style={{ minWidth: orderTableWidth }}
            >
              <GridLayout
                style={{
                  marginRight: 80,
                  padding: 10,
                }}
                gap={{ rows: 0, cols: 10 }}
                cols={orderTableColimn}
              >
                <GridLayoutItem>Select Order</GridLayoutItem>
                <GridLayoutItem>Order No</GridLayoutItem>
                <GridLayoutItem>Product Name</GridLayoutItem>
                <GridLayoutItem>Description</GridLayoutItem>
                <GridLayoutItem>Issue Weight (gm)</GridLayoutItem>
                <GridLayoutItem>Received Weight (gm)</GridLayoutItem>
                <GridLayoutItem>Total Charge</GridLayoutItem>
                <GridLayoutItem>Ghat Weight (gm)</GridLayoutItem>
                <GridLayoutItem>Amount</GridLayoutItem>
                <GridLayoutItem>Finish Order</GridLayoutItem>
                <GridLayoutItem>Verified</GridLayoutItem>
                {dull_receipt_guid && <GridLayoutItem>Image</GridLayoutItem>}
                {/* <GridLayoutItem>Image</GridLayoutItem> */}
                <GridLayoutItem
                  style={{
                    position: "sticky",
                    inset: 0,
                    zIndex: 9,
                    background: "#212529",
                  }}
                ></GridLayoutItem>
              </GridLayout>
            </GridLayoutItem>

            <ShowProcessWiseImageDialog />
            {fieldArrayRenderProps.value &&
              fieldArrayRenderProps.value.length > 0 &&
              fieldArrayRenderProps.value.map(
                (dull_order: any, index: number) => (
                  <GridLayoutItem
                    key={index}
                    style={{ minWidth: orderTableWidth }}
                  >
                    <GridLayout
                      style={{ marginRight: 80 }}
                      gap={{ rows: 0, cols: 10 }}
                      cols={orderTableColimn}
                    >
                      <GridLayoutItem>
                        <Button
                          style={{
                            marginTop: 5,
                          }}
                          onClick={() => handleSelectOrder(index)}
                          fillMode={"solid"}
                          themeColor={"success"}
                          type="button"
                          size={"small"}
                        >
                          Select Order
                        </Button>
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.order_id`}
                          rows={1}
                          placeholder="Order No"
                          component={OrderNoField}
                          disabled={true}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.item_id`}
                          // label="Product Name"
                          disabled={true}
                          placeholder="Select Product"
                          component={FormSelectionField}
                          validator={requiredValidator}
                          options={ItemList?.map((item: IItem) => {
                            return {
                              value: item.id,
                              label: item.item_name,
                            };
                          })}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.item_description`}
                          rows={1}
                          placeholder="Write Here..."
                          component={FormTextArea}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.issue_weight`}
                          disabled={true}
                          placeholder="0.00"
                          type="number"
                          format="n3"
                          component={FormNumericTextField}
                          validator={requiredValidator}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.received_weight`}
                          placeholder="0.00"
                          type="number"
                          format="n3"
                          component={FormNumericTextField}
                          validator={requiredValidator}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.total_charge`}
                          disabled={true}
                          placeholder="0.00"
                          type="number"
                          format="n3"
                          component={FormNumericTextField}
                          validator={requiredValidator}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.ghat_weight`}
                          disabled={true}
                          placeholder="0.00"
                          type="number"
                          format="n3"
                          component={FormNumericTextField}
                          // validator={requiredValidator}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.amount`}
                          disabled={true}
                          placeholder="0.00"
                          type="number"
                          format="n3"
                          component={FormNumericTextField}
                          // validator={requiredValidator}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          name={`${name}.${index}.isfinishorder`}
                          title={"Finish Order"}
                          label={"Finish Order"}
                          size="large"
                          component={FormCheckbox}
                          // validator={requiredValidator}
                          wrapperClassName="enhanced-checklist-dialog__checkbox"
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          name={`${name}.${index}.IsVerified`}
                          title={"Verified"}
                          label={"Verified"}
                          size="large"
                          component={FormCheckbox}
                          validator={requiredValidator}
                          wrapperClassName="enhanced-checklist-dialog__checkbox"
                        />
                      </GridLayoutItem>
                      {dull_receipt_guid && (
                        <GridLayoutItem style={{ display: "flex", gap: 5 }}>
                          <IconButton
                            onClick={() => handleOpenImageUploadDialog(index)}
                            themeColor={"tertiary"}
                            disabled={dull_receipt_guid ? false : true}
                            size={"small"}
                            fillMode={"solid"}
                            type="button"
                            title="Upload Image"
                            style={{
                              marginTop: 5,
                              height: 32,
                              width: 32,
                            }}
                          >
                            <PiUploadSimpleBold style={{ fontSize: "20px" }} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleOpenImage(dull_order.Image)}
                            themeColor={"info"}
                            disabled={dull_order.Image ? false : true}
                            size={"small"}
                            fillMode={"solid"}
                            type="button"
                            title="View Image"
                            style={{
                              marginTop: 5,
                              height: 32,
                              width: 32,
                            }}
                          >
                            <FaEye style={{ fontSize: "20px" }} />
                          </IconButton>
                        </GridLayoutItem>
                      )}
                      <GridLayoutItem
                        style={{
                          position: "sticky",
                          inset: 0,
                          zIndex: 9,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: "white",
                        }}
                      >
                        <IconButton
                          onClick={() => handleOpenDeleteDialog(index)}
                          themeColor={"error"}
                          size={"small"}
                          fillMode={"solid"}
                          type="button"
                          title="Upload Image"
                          style={{
                            height: 32,
                            width: 32,
                          }}
                        >
                          <MdDelete
                            className="absolute-position"
                            style={{ fontSize: "16px" }}
                          />
                        </IconButton>
                      </GridLayoutItem>
                    </GridLayout>
                  </GridLayoutItem>
                ),
              )}
          </GridLayout>
        )}
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{ marginBottom: 10, marginTop: 10, textAlign: "left" }}
        >
          <Button
            fillMode={"solid"}
            themeColor={"primary"}
            disabled={
              !fieldArrayRenderProps.formRenderProps.valueGetter("karigar_id")
            }
            onClick={pushElementInarray}
            type="button"
          >
            Add Order
          </Button>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const ShowProcessWiseImageDialog: React.FC = () => {
  const settings = {
    dots: true,
    infinite: true,
    autoplay: false,
    // centerMode: true,
    autoplaySpeed: 2000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    centerPadding: "0px",
    centerMargin: "0px",
    draggable: true,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const loading = useAppSelector((state) => state.item.loading);
  const ProcessByImageList = useAppSelector(
    (state) => state.item.ProcessByImageList,
  );
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const handleOpenImage = (url: string) => {
    if (url && typeof url === "string") {
      window.open(url);
    }
  };

  return (
    <>
      {dialogName === "show-process-wise-image" && (
        <AppDialog>
          {loading ? (
            <div>
              <LoadingPanel gridRef={gridRef} />
            </div>
          ) : (
            <div style={{ width: "40vw", height: "50vh" }}>
              <GridLayout
                style={{
                  marginLeft: 10,
                }}
                gap={{ rows: 10, cols: 10 }}
              >
                <GridLayoutItem
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography.h4 style={{ margin: "0" }}>
                    Process Images
                  </Typography.h4>
                  <ImCross
                    style={{
                      color: "white",
                      background: "red",
                      borderRadius: "50%",
                      padding: "5px",
                      fontSize: "24px",
                    }}
                    onClick={() => dispatch(closeDialog())}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  {ProcessByImageList && ProcessByImageList?.length === 0 && (
                    <AlertBox alert="No Image Available!" />
                  )}
                </GridLayoutItem>
                <GridLayoutItem
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <div
                    className="slider-container product-view-images"
                    style={{ width: "30vw" }}
                  >
                    {ProcessByImageList && ProcessByImageList.length > 1 ? (
                      <Slider {...settings}>
                        {ProcessByImageList &&
                          ProcessByImageList.length > 0 &&
                          ProcessByImageList.map(
                            (image: any, index: number) => (
                              <div>
                                <div key={index} style={{ height: 300 }}>
                                  <img
                                    src={image.image_name}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                    }}
                                    onClick={() =>
                                      handleOpenImage(image.image_name)
                                    }
                                  />
                                </div>
                                {/* <Typography.h5 style={{ textAlign: "center" }}>
                                {image.title}
                              </Typography.h5> */}
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 5,
                                  }}
                                >
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Title
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.title || "N/A"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Process Name
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.process_name || "N/A"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Main Image
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.isreadyimage ? "YES" : "NO"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                      </Slider>
                    ) : (
                      <>
                        {ProcessByImageList &&
                          ProcessByImageList.length > 0 &&
                          ProcessByImageList.map(
                            (image: any, index: number) => (
                              // <div
                              //   style={{
                              //     width: "40%",
                              //     margin: "auto",
                              //   }}
                              //   key={index}
                              // >
                              //   <img
                              //     src={image.image_name}
                              //     style={{
                              //       width: "100%",
                              //       height: "auto",
                              //       objectFit: "contain",
                              //     }}
                              //     onClick={() =>
                              //       handleOpenImage(image.image_name)
                              //     }
                              //   />
                              // </div>
                              <div>
                                <div key={index} style={{ height: 300 }}>
                                  <img
                                    src={image.image_name}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                    }}
                                    onClick={() =>
                                      handleOpenImage(image.image_name)
                                    }
                                  />
                                </div>
                                {/* <Typography.h5 style={{ textAlign: "center" }}>
                               {image.title}
                             </Typography.h5> */}
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 5,
                                  }}
                                >
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Title
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.title || "N/A"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Process Name
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.process_name || "N/A"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Main Image
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.isreadyimage ? "YES" : "NO"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                      </>
                    )}
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </div>
          )}
        </AppDialog>
      )}
    </>
  );
};

interface ImageUploadDialogProps {
  fieldArrayRenderProps: FieldArrayRenderProps;
}
const ImageUploadDialog: React.FC<ImageUploadDialogProps> = ({
  fieldArrayRenderProps,
}) => {
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const DullReceiptOrderItemIndex = useAppSelector(
    (state) => state.dullreceipt.DullReceiptOrderItemIndex,
  );
  const location = useLocation();
  const dull_receipt_guid = location?.state?.dull_receipt_guid;
  const dispatch = useAppDispatch();
  const handleSubmit = async (values: any) => {
    const Image = values.image;
    const IsAddImage = values.IsAddImage || false;
    const ID = fieldArrayRenderProps.value?.[DullReceiptOrderItemIndex!]?.id;
    const ImageData = new FormData();
    let binaryChitthiImage = "";
    ImageData.append("id", ID);
    ImageData.append("IsAddImage", IsAddImage);
    if (
      typeof Image !== "string" &&
      Array.isArray(Image) &&
      Image?.length > 0
    ) {
      binaryChitthiImage = Image[0]?.getRawFile();
      ImageData.append("image", binaryChitthiImage);
    }
    if (
      typeof Image !== "string" &&
      Array.isArray(Image) &&
      Image?.length > 0
    ) {
      const res = await dispatch(updateDullReceiptImage(ImageData));
      if (res.meta.requestStatus === "fulfilled") {
        dispatch(closeDialog());
        dispatch(setDullReceiptOrderItemIndex(-1));
        if (dull_receipt_guid) {
          dispatch(getDullReceiptById(dull_receipt_guid));
        }
      }
    }
  };

  const handleCloseDialog = () => {
    dispatch(closeDialog());
    dispatch(setDullReceiptOrderItemIndex(-1));
  };

  return (
    <>
      {dialogName === "show-image-upload-dialog" && (
        <AppDialog>
          <div style={{ width: "500px" }}>
            <Form
              onSubmit={handleSubmit}
              initialValues={{ imgae: "" }}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <GridLayout
                    style={{
                      marginLeft: 10,
                    }}
                    gap={{ rows: 10, cols: 10 }}
                  >
                    <GridLayoutItem
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography.h4 style={{ margin: "0" }}>
                        Image Upload
                      </Typography.h4>
                      <ImCross
                        style={{
                          color: "white",
                          background: "red",
                          borderRadius: "50%",
                          padding: "5px",
                          fontSize: "24px",
                        }}
                        onClick={() => {
                          dispatch(closeDialog());
                          dispatch(setDullReceiptOrderItemIndex(-1));
                        }}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        // imageWidth={60}
                        // imageHeight={60}
                        name={`image`}
                        label="Image"
                        type="file"
                        accept="image/*"
                        component={FormImageUpload}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`IsAddImage`}
                        title={"Is Add Image"}
                        label={"Is Add Image"}
                        size="large"
                        component={FormCheckbox}
                        wrapperClassName="enhanced-checklist-dialog__checkbox"
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      style={{
                        textAlign: "end",
                        marginTop: "20px",
                        width: "100%",
                      }}
                    >
                      <ButtonWithLoading
                        label="Upload"
                        type="submit"
                        disabled={!formRenderProps.allowSubmit}
                        // loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 4 }}
                        onClick={handleCloseDialog}
                      >
                        Cancel
                      </Button>
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />
          </div>
        </AppDialog>
      )}
    </>
  );
};

export default DullReceiptOrderItemArray;
